import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api, extractError, API_BASE, TOKEN_KEY } from "../../lib/api";
import { parseAmountToCents, formatMoney, toDateInputValue, dateInputToIso } from "../../lib/money";
import { useAuth } from "../../contexts/AuthContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Card, CardContent } from "../../components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { Alert, AlertDescription } from "../../components/ui/alert";
import { Receipt as ReceiptIcon, X, Camera, ChevronLeft, Truck, Container, User as UserIcon } from "lucide-react";
import { toast } from "sonner";
import TmsStatusBadge from "../../components/TmsStatusBadge";

const NO_PROJECT = "__none__";

const TMS_KINDS = [
  { key: "trucks", api: "trucks", singular: "truck", Icon: Truck },
  { key: "trailers", api: "trailers", singular: "trailer", Icon: Container },
  { key: "drivers", api: "drivers", singular: "driver", Icon: UserIcon },
];

function FieldError({ children, testId = "field-error" }) {
  if (!children) return null;
  return <p className="mt-1 text-xs text-rose-600" data-testid={testId}>{children}</p>;
}

function Required() {
  return <span className="ml-0.5 text-rose-600" aria-label="required">*</span>;
}

/* ------------------------------------------------------------------ */
/*  TmsSection — typeahead + chips for one kind (trucks|trailers|drivers) */
/* ------------------------------------------------------------------ */
function TmsSection({ kind, items, onAdd, onRemove }) {
  const { key, api: apiKind, singular, Icon } = kind;
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const debouncedQ = useDebounce(q, 300);

  useEffect(() => {
    let cancelled = false;
    if (debouncedQ.length < 1) { setResults([]); return; }
    setLoading(true);
    api.get(`/tms/${apiKind}/search`, { params: { q: debouncedQ, limit: 10 } })
      .then((r) => { if (!cancelled) setResults(r.data || []); })
      .catch(() => { if (!cancelled) setResults([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [debouncedQ, apiKind]);

  const pick = (r) => {
    if (items.some((it) => it.id === r.id)) {
      setQ(""); setResults([]);
      return; // dedupe
    }
    onAdd({ id: r.id, label: r.label, status: r.status || "" });
    setQ(""); setResults([]);
  };

  // Phase 12d: drivers, trailers, and trucks all now surface a status badge.
  // ONLINE / OFF DUTY / Active render with no badge (TmsStatusBadge returns
  // null for those) — only meaningful states (OFFLINE, Inactive, Leased Out,
  // Repair, Crashed) get visual weight.
  const showBadges = true;

  return (
    <div className="rounded-md border border-slate-200 bg-white p-3" data-testid={`tms-section-${key}`}>
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
          <Icon className="h-4 w-4 text-[#0F1E3D]" />
          <span className="capitalize">{key}</span>
          {items.length > 0 && (
            <span className="rounded-full bg-[#0F1E3D]/10 px-2 py-0.5 text-[10px] font-semibold text-[#0F1E3D]"
              data-testid={`tms-${key}-count`}>{items.length}</span>
          )}
        </div>
      </div>

      {items.length > 0 ? (
        <div className="mb-2 flex flex-wrap gap-1.5" data-testid={`tms-${key}-chips`}>
          {items.map((it) => (
            <span key={it.id} data-testid={`tms-${key}-chip-${it.id}`}
              className="inline-flex max-w-full items-center gap-1.5 rounded-full bg-[#0F1E3D]/5 px-2.5 py-1 text-xs text-[#0F1E3D] ring-1 ring-[#0F1E3D]/15">
              <Icon className="h-3 w-3 flex-shrink-0" />
              <span className="truncate">{it.label}</span>
              {showBadges && <TmsStatusBadge status={it.status} testId={`tms-${key}-chip-status-${it.id}`} />}
              <button type="button" data-testid={`tms-${key}-remove-${it.id}`}
                onClick={() => onRemove(it.id)} aria-label={`Remove ${it.label}`}
                className="ml-0.5 rounded-full text-slate-400 hover:text-rose-600">
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      ) : (
        <p className="mb-2 text-xs italic text-slate-400" data-testid={`tms-${key}-empty`}>
          No {key} linked
        </p>
      )}

      <Input
        value={q} onChange={(e) => setQ(e.target.value)}
        placeholder={`Search ${singular}s…`}
        data-testid={`tms-${key}-search`} autoComplete="off"
      />
      {loading && <p className="mt-1 text-xs text-slate-400" data-testid={`tms-${key}-loading`}>Searching…</p>}
      {!loading && debouncedQ.length >= 1 && results.length === 0 && (
        <p className="mt-1 text-xs text-slate-400" data-testid={`tms-${key}-no-results`}>No matches</p>
      )}
      {results.length > 0 && (
        <ul className="mt-1 max-h-44 overflow-y-auto rounded-md border border-slate-200 bg-white shadow-sm"
          data-testid={`tms-${key}-results`}>
          {results.map((r) => (
            <li key={r.id}>
              <button type="button" data-testid={`tms-${key}-result-${r.id}`}
                onClick={() => pick(r)}
                className="flex w-full items-center gap-2 border-b border-slate-100 px-3 py-2 text-left text-sm last:border-0 hover:bg-slate-50">
                <span className="flex-1 truncate">{r.label}</span>
                {showBadges && <TmsStatusBadge status={r.status} testId={`tms-${key}-result-status-${r.id}`} />}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function useDebounce(value, delay) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

/* ------------------------------------------------------------------ */
/*  ExpenseForm                                                        */
/* ------------------------------------------------------------------ */
export default function ExpenseForm() {
  const { id: editId } = useParams();
  const isEdit = Boolean(editId);
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const chaseRequestId = searchParams.get("comment_request_id");

  const [cards, setCards] = useState([]);
  const [categories, setCategories] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loadingLookups, setLoadingLookups] = useState(true);
  const [loadError, setLoadError] = useState("");

  const [amount, setAmount] = useState("");
  const [cardId, setCardId] = useState("");
  const [vendor, setVendor] = useState("");
  const [vendorSuggestions, setVendorSuggestions] = useState([]);
  const [showVendorList, setShowVendorList] = useState(false);
  const [dateValue, setDateValue] = useState(toDateInputValue(new Date()));
  const [categoryId, setCategoryId] = useState("");
  // Phase 13: per-user vendor→category memory.
  // hintInfo is set when we auto-fill category from the last-used mapping for
  // the current vendor; cleared when the user manually changes category or
  // when the hint lookup returns nothing. Never used in edit mode.
  const [hintInfo, setHintInfo] = useState(null); // { vendor_display, category_id }
  const [companyId, setCompanyId] = useState(NO_PROJECT);
  const [departmentId, setDepartmentId] = useState(NO_PROJECT);
  const [trucks, setTrucks] = useState([]);
  const [trailers, setTrailers] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [tmsOverride, setTmsOverride] = useState(false);
  // Phase 11: chargeType replaces the boolean chargeDriver. We still send
  // charge_driver in the payload for downstream consumers — the backend
  // computes it from charge_type but accepts either.
  const [chargeType, setChargeType] = useState("internal");
  const [lesseeName, setLesseeName] = useState("");
  const [comment, setComment] = useState("");
  const [receiptId, setReceiptId] = useState(null);
  const [receiptPreview, setReceiptPreview] = useState(null);
  const [uploadingReceipt, setUploadingReceipt] = useState(false);
  // Phase 13c — Toll app iframe prefill state.
  // Populated when the user arrives at /expenses/new?prefill=<id>. The
  // receipt_url pass-through lets the backend ingest it into GridFS on
  // save, so we don't need to fetch the file on the client.
  const [prefillId, setPrefillId] = useState("");
  const [prefillReceiptUrl, setPrefillReceiptUrl] = useState("");
  const [prefillIdempotencyKey, setPrefillIdempotencyKey] = useState("");
  const [prefillBanner, setPrefillBanner] = useState(""); // "loaded" | "expired" | "notfound" | ""
  const [prefillCategoryMiss, setPrefillCategoryMiss] = useState("");
  const [prefillTmsWarnings, setPrefillTmsWarnings] = useState([]);
  const fileRef = useRef(null);

  const [attemptedSubmit, setAttemptedSubmit] = useState(false);
  const [touched, setTouched] = useState({ department: false, comment: false, company: false });
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});

  // Load lookups + (when editing) the expense
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingLookups(true); setLoadError("");
      try {
        const [c, cat, proj, comps] = await Promise.all([
          api.get("/cards/available"),
          api.get("/categories/active"),
          api.get("/departments/active"),
          api.get("/companies/active"),
        ]);
        if (cancelled) return;
        setCards(c.data); setCategories(cat.data); setDepartments(proj.data); setCompanies(comps.data);
        if (isEdit) {
          const { data } = await api.get(`/expenses/${editId}`);
          setAmount((data.amount_cents / 100).toFixed(2));
          setCardId(data.card_id);
          setVendor(data.vendor);
          setDateValue(toDateInputValue(data.occurred_at));
          setCategoryId(data.category_id);
          setCompanyId(data.company_id || NO_PROJECT);
          setDepartmentId(data.department_id || NO_PROJECT);
          setTrucks(data.trucks || []);
          setTrailers(data.trailers || []);
          setDrivers(data.drivers || []);
          setTmsOverride(Boolean(data.tms_override));
          setChargeType(data.charge_type || (data.charge_driver ? "driver" : "internal"));
          setLesseeName(data.lessee_name || "");
          setComment(data.comment || "");
          setReceiptId(data.receipt_image_id || null);
          if (data.receipt_image_id) setReceiptPreview(`${API_BASE}/receipts/${data.receipt_image_id}`);
        }
      } catch (e) {
        if (!cancelled) setLoadError(extractError(e));
      } finally {
        if (!cancelled) setLoadingLookups(false);
      }
    })();
    return () => { cancelled = true; };
  }, [editId, isEdit]);

  // Pre-fill Department from user's default_department_id (new expense only)
  const defaultDeptApplied = useRef(false);
  useEffect(() => {
    if (isEdit) return;
    if (defaultDeptApplied.current) return;
    if (loadingLookups) return;
    if (!user?.default_department_id) return;
    if (departmentId !== NO_PROJECT) return;
    const match = departments.find((d) => d.id === user.default_department_id);
    if (!match) return;
    defaultDeptApplied.current = true;
    const t = setTimeout(() => setDepartmentId(match.id), 0);
    return () => clearTimeout(t);
  }, [isEdit, loadingLookups, user, departments, departmentId]);

  // Pre-fill Company from user's default_company_id (new expense only)
  const defaultCompanyApplied = useRef(false);
  useEffect(() => {
    if (isEdit) return;
    if (defaultCompanyApplied.current) return;
    if (loadingLookups) return;
    if (!user?.default_company_id) return;
    if (companyId !== NO_PROJECT) return;
    const match = companies.find((c) => c.id === user.default_company_id);
    if (!match) return;
    defaultCompanyApplied.current = true;
    const t = setTimeout(() => setCompanyId(match.id), 0);
    return () => clearTimeout(t);
  }, [isEdit, loadingLookups, user, companies, companyId]);

  // Phase 4: prefill from chase URL params
  useEffect(() => {
    if (isEdit || loadingLookups) return;
    const cardParam = searchParams.get("card_id");
    const amtParam = searchParams.get("amount_cents");
    const dateParam = searchParams.get("occurred_at");
    const vendorParam = searchParams.get("vendor");
    if (cardParam && cards.some((c) => c.id === cardParam)) setCardId(cardParam);
    if (amtParam) setAmount((parseInt(amtParam, 10) / 100).toFixed(2));
    if (dateParam) setDateValue(dateParam);
    if (vendorParam) setVendor(vendorParam);
  }, [isEdit, loadingLookups, cards, searchParams]);

  // Phase 13c — Toll app iframe deep-link hydration.
  // /expenses/new?prefill=<id> fetches a server-stored payload and applies
  // every recognised field. Failures never block the form — the user still
  // sees a usable blank form with a small banner explaining what happened.
  const prefillApplied = useRef(false);
  useEffect(() => {
    if (isEdit || loadingLookups) return;
    if (prefillApplied.current) return;
    const pid = searchParams.get("prefill");
    if (!pid) return;
    prefillApplied.current = true;

    (async () => {
      try {
        const { data } = await api.get(`/prefill/${pid}`);
        setPrefillId(pid);
        setPrefillBanner("loaded");
        setPrefillIdempotencyKey(data.idempotency_key || "");
        if (data.amount != null) setAmount(String(Number(data.amount).toFixed(2)));
        if (data.vendor) setVendor(String(data.vendor));
        if (data.date) {
          try { setDateValue(toDateInputValue(new Date(data.date))); }
          catch (err) { console.error("prefill: setDateValue failed", err); /* leave default */ }
        }
        if (data.comment) setComment(String(data.comment));
        if (data.category) {
          const wanted = String(data.category).trim().toLowerCase();
          const match = categories.find((c) => c.name.toLowerCase() === wanted);
          if (match) setCategoryId(match.id);
          else setPrefillCategoryMiss(String(data.category));
        }
        if (data.receipt_url) setPrefillReceiptUrl(String(data.receipt_url));

        // TMS pickers — resolve each label to an Alvys entity via the search API.
        const warnings = [];
        // Phase 13f — TMS resolver.
        // Resolution order (per spec):
        //   1. If `external_id` is on the incoming ref → search by that Id,
        //      match `it.id === external_id`. Skip the label-matching path.
        //   2. Otherwise, `bare_target` = number-only prefix of the label
        //      (`"#3551 — 53' Reefer"` → `"3551"`). Query the search API
        //      with the bare number, then EXACT-match candidate labels'
        //      bare numbers to disambiguate `#3551` vs `#903551`.
        // Only auto-select when exactly one candidate matches. Zero → not-
        // found warning; >1 → ambiguity warning; never auto-pick arbitrarily.
        //
        // bareUnit: strip leading `#`, split on whitespace / em-dash / hyphen,
        // take the first token, lowercase. Hyphen last in the char class is
        // a literal hyphen, not a range — do NOT reorder.
        const bareUnit = (s) =>
          String(s ?? "").trim().replace(/^#/, "").split(/[\s—-]/)[0].toLowerCase();
        const kindLabel = (k) => k.slice(0, -1).replace(/^./, (c) => c.toUpperCase());
        const resolveOne = async (kind, refs, setter, currentList) => {
          if (!refs || refs.length === 0) return;
          const resolved = [...currentList];
          for (const r of refs) {
            const rawLabel = String(r.label || "").trim();
            const extId = String(r.external_id || "").trim();
            const displayLabel = rawLabel || extId;
            if (!rawLabel && !extId) continue;

            // Fast path — external_id short-circuit. The Alvys search API
            // doesn't index by external_id, so per spec we skip the search
            // entirely and construct the chip directly from the ref. Label
            // shown to the user is whatever Toll put in `label` (falling
            // back to the id if none).
            if (extId) {
              const synthetic = {
                id: extId,
                label: rawLabel || extId,
                type: kind.slice(0, -1),  // "trucks" -> "truck"
              };
              if (!resolved.some((x) => x.id === synthetic.id)) resolved.push(synthetic);
              continue;
            }

            // Fallback — bare-unit exact match on label.
            const target = bareUnit(rawLabel);
            if (!target) {
              warnings.push(`${kindLabel(kind)} ${displayLabel} not found in Alvys — please pick manually.`);
              continue;
            }
            try {
              // Send the BARE number to Alvys search (not the raw "#3551")
              // to avoid Alvys-side false negatives on the `#` prefix.
              const { data: results } = await api.get(`/tms/${kind}/search`, { params: { q: target, limit: 10 } });
              const matches = (results || []).filter((it) => bareUnit(it.label || it.number) === target);
              if (matches.length === 1) {
                const hit = matches[0];
                if (!resolved.some((x) => x.id === hit.id)) resolved.push(hit);
              } else if (matches.length === 0) {
                warnings.push(`${kindLabel(kind)} ${displayLabel} not found in Alvys — please pick manually.`);
              } else {
                warnings.push(`Multiple matches for ${displayLabel} — please pick manually.`);
              }
            } catch (_) {
              warnings.push(`Could not check ${kind} ${displayLabel} in Alvys — please pick manually.`);
            }
          }
          setter(resolved);
        };
        await resolveOne("trucks", data.tms?.trucks, setTrucks, trucks);
        await resolveOne("trailers", data.tms?.trailers, setTrailers, trailers);
        await resolveOne("drivers", data.tms?.drivers, setDrivers, drivers);
        setPrefillTmsWarnings(warnings);
      } catch (err) {
        const code = err?.response?.status;
        if (code === 410) setPrefillBanner("expired");
        else setPrefillBanner("notfound");
      }
    })();
  // Intentionally scoped to the load conditions — `trucks/trailers/drivers`
  // are read once at hydration time and not part of the deps to prevent
  // re-running when the user later edits pickers.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, loadingLookups, categories, searchParams]);

  // Vendor autocomplete
  useEffect(() => {
    if (!vendor || vendor.length < 1) { setVendorSuggestions([]); return; }
    const handle = setTimeout(async () => {
      try {
        const { data } = await api.get("/expenses/vendors/recent", { params: { q: vendor, limit: 8 } });
        setVendorSuggestions(data);
      } catch (err) {
        // Non-critical autocomplete request; surface in console for diagnosis
        // without disrupting the form UX.
        console.warn("ExpenseForm: vendor autocomplete failed", err);
      }
    }, 200);
    return () => clearTimeout(handle);
  }, [vendor]);

  const segmentedCards = cards.length > 0 && cards.length <= 4;

  // Auto-revert charge_type when its preconditions disappear (Phase 11).
  //   - "driver"  needs ≥1 driver  → revert to "internal" otherwise.
  //   - "lessee"  needs ≥1 truck or trailer AND a lessee_name → revert to
  //     "internal" (and clear lessee_name) when equipment is removed.
  // A small amber notice is surfaced so the worker sees the change.
  const [autoRevertNotice, setAutoRevertNotice] = useState("");
  useEffect(() => {
    if (chargeType === "driver" && drivers.length === 0) {
      setChargeType("internal");
      setAutoRevertNotice("Driver removed — charge type reverted to Internal cost.");
    } else if (chargeType === "lessee" && trucks.length === 0 && trailers.length === 0) {
      setChargeType("internal");
      setLesseeName("");
      setAutoRevertNotice("All trucks/trailers removed — lessee charge cleared.");
    }
  }, [drivers, trucks, trailers, chargeType]);
  // Clear the notice once the user touches charge_type or links new equipment.
  useEffect(() => {
    if (!autoRevertNotice) return;
    const t = setTimeout(() => setAutoRevertNotice(""), 6000);
    return () => clearTimeout(t);
  }, [autoRevertNotice]);

  // ---- Mandatory-field checks ----
  const commentTrimmed = comment.trim();
  const hasTms = trucks.length + trailers.length + drivers.length > 0 || tmsOverride;
  const departmentValid = departmentId && departmentId !== NO_PROJECT;
  const companyValid = companyId && companyId !== NO_PROJECT;
  const commentValid = commentTrimmed.length > 0 && commentTrimmed.length <= 1000;

  const errors = useMemo(() => {
    const errs = {};
    const showDept = attemptedSubmit || touched.department;
    const showComment = attemptedSubmit || touched.comment;
    const showCompany = attemptedSubmit || touched.company;
    if (showCompany && !companyValid) errs.company = "Company is required";
    if (showDept && !departmentValid) errs.department = "Department is required";
    if (showComment && !commentValid) {
      if (commentTrimmed.length === 0) errs.comment = "Comment is required";
      else if (commentTrimmed.length > 1000) errs.comment = "Comment too long (max 1000).";
    }
    if (attemptedSubmit && !hasTms) errs.tms = "Link at least one truck, trailer, or driver.";
    return errs;
  }, [attemptedSubmit, touched, departmentValid, companyValid, commentValid, commentTrimmed, hasTms]);

  const otherValid = () => {
    const errs = {};
    const cents = parseAmountToCents(amount);
    if (!cents) errs.amount = "Enter a valid amount greater than 0.";
    if (!cardId) errs.card = "Pick a card.";
    if (!vendor.trim()) errs.vendor = "Vendor is required.";
    else if (vendor.trim().length > 120) errs.vendor = "Vendor too long (max 120).";
    if (!dateValue) errs.date = "Date is required.";
    if (!categoryId) errs.category = "Pick a category.";
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const submitEnabled = companyValid && departmentValid && commentValid && hasTms && !submitting
    && (chargeType !== "lessee" || lesseeName.trim().length > 0);

  // ---- Receipt handlers (unchanged) ----
  const onPickFile = () => fileRef.current?.click();

  const onFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const isPdf = file.type === "application/pdf";
    const cap = isPdf ? 15 * 1024 * 1024 : 10 * 1024 * 1024;
    if (file.size > cap) {
      toast.error(`Receipt too large — max ${isPdf ? "15MB" : "10MB"} for ${isPdf ? "PDF" : "image"}.`);
      return;
    }
    if (!["image/jpeg", "image/png", "image/webp", "image/heic", "application/pdf"].includes(file.type)) {
      toast.error(`Unsupported file type (${file.type || "unknown"}). Use JPG, PNG, WebP, HEIC, or PDF.`);
      return;
    }
    setUploadingReceipt(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const token = localStorage.getItem(TOKEN_KEY);
      const res = await fetch(`${API_BASE}/receipts`, {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: fd,
      });
      if (!res.ok) {
        const detail = await res.json().catch(() => ({}));
        throw new Error(detail.detail || `Upload failed (${res.status})`);
      }
      const data = await res.json();
      setReceiptId(data.id);
      const url = URL.createObjectURL(file);
      setReceiptPreview(url);
      toast.success("Receipt uploaded");
    } catch (err) {
      toast.error(err.message || "Receipt upload failed");
    } finally {
      setUploadingReceipt(false);
    }
  };

  const removeReceipt = () => {
    setReceiptId(null);
    if (receiptPreview && receiptPreview.startsWith("blob:")) URL.revokeObjectURL(receiptPreview);
    setReceiptPreview(null);
  };

  // ---- TMS chip handlers ----
  const addTo = (kind, item) => {
    const setter = kind === "trucks" ? setTrucks : kind === "trailers" ? setTrailers : setDrivers;
    setter((prev) => (prev.some((x) => x.id === item.id) ? prev : [...prev, item]));
  };
  const removeFrom = (kind, id) => {
    const setter = kind === "trucks" ? setTrucks : kind === "trailers" ? setTrailers : setDrivers;
    setter((prev) => prev.filter((x) => x.id !== id));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setAttemptedSubmit(true);
    setFormError("");
    if (!otherValid()) return;
    if (!departmentValid || !commentValid || !hasTms) return; // also enforced by disabled button
    setSubmitting(true);
    try {
      const payload = {
        card_id: cardId,
        amount_cents: parseAmountToCents(amount),
        currency: "USD",
        vendor: vendor.trim(),
        occurred_at: dateInputToIso(dateValue),
        category_id: categoryId,
        company_id: companyId,
        department_id: departmentId,
        trucks, trailers, drivers,
        tms_override: tmsOverride,
        // Phase 11 source of truth — backend mirrors charge_driver from this.
        charge_type: chargeType,
        lessee_name: chargeType === "lessee" ? lesseeName.trim() : null,
        comment: commentTrimmed,
        receipt_image_id: receiptId || null,
        // Phase 13c — Toll app URL pass-through. Only sent when there's no
        // manual upload id (backend rejects both together).
        receipt_url: (!receiptId && prefillReceiptUrl) ? prefillReceiptUrl : undefined,
      };
      if (isEdit) {
        await api.patch(`/expenses/${editId}`, payload);
        toast.success("Expense updated");
        navigate(`/expenses/${editId}`);
      } else {
        const { data } = await api.post("/expenses", payload);
        // Phase 13c — notify the Toll parent when embedded in an iframe.
        // targetOrigin is strict to prevent leaking the id to any other site.
        if (window.parent && window.parent !== window) {
          try {
            window.parent.postMessage(
              { type: "EXPENSE_LOGGED",
                expense_id: data.id,
                idempotency_key: prefillIdempotencyKey || null,
                status: "created" },
              "https://tolls.aftfreight.com"
            );
          } catch (err) { console.error("postMessage EXPENSE_LOGGED failed", err); /* fail silent — postMessage must not block navigation */ }
        }
        if (chaseRequestId) {
          try {
            await api.post(`/comment-requests/${chaseRequestId}/answer`, { expense_id: data.id });
            toast.success("Expense logged and linked to the chase");
            navigate("/inbox");
            return;
          } catch (chaseErr) {
            toast.error(extractError(chaseErr, "Expense saved, but could not link to chase"));
          }
        }
        toast.success("Expense logged");
        navigate(user?.role === "worker" ? "/" : `/expenses/${data.id}`);
      }
    } catch (err) {
      setFormError(extractError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const totalPreview = useMemo(() => {
    const cents = parseAmountToCents(amount);
    return cents ? formatMoney(cents) : "—";
  }, [amount]);

  if (loadingLookups) {
    return <div className="text-center text-slate-500" data-testid="form-loading">Loading…</div>;
  }
  if (loadError) {
    return <Alert variant="destructive"><AlertDescription>{loadError}</AlertDescription></Alert>;
  }
  if (cards.length === 0) {
    return (
      <Alert variant="destructive" data-testid="no-cards-alert">
        <AlertDescription>
          You have no cards available. Contact your admin to assign one before logging expenses.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div data-testid="expense-form-page">
      <button onClick={() => navigate(-1)}
        className="mb-4 inline-flex items-center text-sm text-slate-500 hover:text-slate-900"
        data-testid="back-btn">
        <ChevronLeft className="mr-1 h-4 w-4" /> Back
      </button>

      <h1 className="mb-6 text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">
        {isEdit ? "Edit expense" : "Log expense"}
      </h1>

      <form onSubmit={onSubmit} className="space-y-5 pb-28 sm:pb-0" data-testid="expense-form" noValidate>
        {/* Phase 13c — Toll app prefill banner. */}
        {prefillBanner === "loaded" && (
          <div className="rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-sm text-sky-900"
            data-testid="prefill-banner">
            <div className="flex items-center justify-between gap-3">
              <span>Prefilled from Toll app.</span>
              <button type="button" className="text-xs font-medium text-sky-700 hover:underline"
                data-testid="prefill-clear-btn"
                onClick={() => {
                  // Reset the hydrated fields to defaults; keep the user's
                  // dept/company auto-selections since those are theirs.
                  setAmount(""); setVendor(""); setDateValue(toDateInputValue(new Date()));
                  setComment(""); setCategoryId(""); setTrucks([]); setTrailers([]); setDrivers([]);
                  setPrefillReceiptUrl(""); setPrefillBanner("");
                  setPrefillCategoryMiss(""); setPrefillTmsWarnings([]);
                }}>Clear</button>
            </div>
            {prefillCategoryMiss && (
              <div className="mt-1 text-xs text-sky-800" data-testid="prefill-category-miss">
                Category &ldquo;{prefillCategoryMiss}&rdquo; not found in your list — please pick one.
              </div>
            )}
            {prefillTmsWarnings.map((w, i) => (
              <div key={i} className="mt-1 text-xs text-sky-800" data-testid={`prefill-tms-warn-${i}`}>{w}</div>
            ))}
            {prefillReceiptUrl && (
              <div className="mt-1 text-xs text-sky-800" data-testid="prefill-receipt-note">
                Receipt: prefilled from Toll (will attach on save).
              </div>
            )}
          </div>
        )}
        {prefillBanner === "expired" && (
          <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900"
            data-testid="prefill-banner-expired">
            Prefill link expired — please refresh from the Toll app.
          </div>
        )}
        {prefillBanner === "notfound" && (
          <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900"
            data-testid="prefill-banner-notfound">
            Prefill link not found.
          </div>
        )}
        {/* Amount */}
        <Card className="border-slate-200">
          <CardContent className="pt-6">
            <Label htmlFor="ex-amount" className="text-slate-700">Amount (USD)</Label>
            <div className="mt-2 flex items-baseline gap-2">
              <span className="text-2xl text-slate-400">$</span>
              <Input id="ex-amount" inputMode="decimal" placeholder="0.00"
                value={amount} onChange={(e) => setAmount(e.target.value)}
                className="border-0 border-b border-slate-200 px-0 text-3xl font-semibold focus-visible:ring-0"
                data-testid="expense-amount-input" />
            </div>
            <FieldError>{fieldErrors.amount}</FieldError>
            <p className="mt-2 text-xs text-slate-500">Stored as {totalPreview}</p>
          </CardContent>
        </Card>

        {/* Card */}
        <div className="space-y-2">
          <Label className="text-slate-700">Card</Label>
          {segmentedCards ? (
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4" data-testid="card-segments">
              {cards.map((c) => (
                <button type="button" key={c.id} onClick={() => setCardId(c.id)}
                  data-testid={`card-segment-${c.last4}`}
                  className={`rounded-md border px-3 py-2 text-left text-sm transition-colors ${
                    cardId === c.id ? "border-[#0F1E3D] bg-[#0F1E3D]/5 text-[#0F1E3D]" : "border-slate-200 bg-white hover:bg-slate-50"
                  }`}>
                  <p className="font-medium leading-tight">{c.label}</p>
                  <p className="mt-0.5 font-mono text-xs text-slate-500">•••• {c.last4}</p>
                </button>
              ))}
            </div>
          ) : (
            <Select value={cardId} onValueChange={setCardId}>
              <SelectTrigger data-testid="card-select"><SelectValue placeholder="Select card…" /></SelectTrigger>
              <SelectContent>
                {cards.map((c) => (
                  <SelectItem key={c.id} value={c.id} data-testid={`card-option-${c.last4}`}>
                    {c.label} (•••• {c.last4})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <FieldError>{fieldErrors.card}</FieldError>
        </div>

        {/* Vendor */}
        <div className="space-y-2 relative">
          <Label htmlFor="ex-vendor" className="text-slate-700">Vendor</Label>
          <Input id="ex-vendor" value={vendor}
            onChange={(e) => { setVendor(e.target.value); setShowVendorList(true); }}
            onFocus={() => setShowVendorList(true)}
            onBlur={async () => {
              setTimeout(() => setShowVendorList(false), 150);
              // Phase 13: auto-fill category from per-user memory.
              // Skip on edit — saved category is source of truth.
              // Skip if user already picked a category — never override.
              if (isEdit) return;
              const v = (vendor || "").trim();
              if (!v) { setHintInfo(null); return; }
              if (categoryId) return;
              try {
                const { data } = await api.get("/vendor-category-hint", { params: { vendor: v } });
                if (data && data.category_id) {
                  setCategoryId(data.category_id);
                  setHintInfo({ vendor_display: data.vendor_display, category_id: data.category_id });
                }
              } catch (_) {
                // 404 (no hint) or transient — fail silent, hint UI stays hidden.
                setHintInfo(null);
              }
            }}
            placeholder="e.g., Pilot Travel Center" maxLength={120}
            data-testid="expense-vendor-input" autoComplete="off" />
          {showVendorList && vendorSuggestions.length > 0 && (
            <div className="absolute z-10 mt-1 max-h-48 w-full overflow-y-auto rounded-md border border-slate-200 bg-white shadow-md"
              data-testid="vendor-suggestions">
              {vendorSuggestions.map((v) => (
                <button key={v.vendor} type="button"
                  onMouseDown={(e) => { e.preventDefault(); setVendor(v.vendor); setShowVendorList(false); }}
                  className="block w-full px-3 py-2 text-left text-sm hover:bg-slate-50"
                  data-testid={`vendor-suggestion-${v.vendor}`}>{v.vendor}</button>
              ))}
            </div>
          )}
          <FieldError>{fieldErrors.vendor}</FieldError>
        </div>

        {/* Date */}
        <div className="space-y-2">
          <Label htmlFor="ex-date" className="text-slate-700">Date</Label>
          <Input id="ex-date" type="date" value={dateValue}
            onChange={(e) => setDateValue(e.target.value)}
            max={toDateInputValue(new Date(Date.now() + 24 * 3600 * 1000))}
            data-testid="expense-date-input" />
          <FieldError>{fieldErrors.date}</FieldError>
        </div>

        {/* Category */}
        <div className="space-y-2">
          <Label className="text-slate-700">Category</Label>
          <div className="flex flex-wrap gap-2" data-testid="category-chips">
            {categories.map((c) => (
              <button key={c.id} type="button" onClick={() => {
                setCategoryId(c.id);
                // Hide the "Auto-filled" hint the moment the user makes a
                // manual choice — the claim is no longer accurate.
                if (hintInfo && c.id !== hintInfo.category_id) setHintInfo(null);
              }}
                data-testid={`category-chip-${c.name}`}
                className={`rounded-full border px-3 py-1.5 text-sm transition-colors ${
                  categoryId === c.id ? "border-[#0F1E3D] bg-[#0F1E3D] text-white" : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                }`}>{c.name}</button>
            ))}
          </div>
          {hintInfo && categoryId === hintInfo.category_id && (
            <div className="mt-1 text-xs italic text-slate-500" data-testid="category-hint">
              Auto-filled from last &ldquo;{hintInfo.vendor_display}&rdquo; expense
            </div>
          )}
          <FieldError>{fieldErrors.category}</FieldError>
        </div>

        {/* Company — required */}
        <div className="space-y-2">
          <Label className="text-slate-700">Company<Required /></Label>
          <Select value={companyId}
            onValueChange={(v) => { setCompanyId(v); setTouched((t) => ({ ...t, company: true })); }}>
            <SelectTrigger data-testid="company-select"
              onBlur={() => setTouched((t) => ({ ...t, company: true }))}
              className={errors.company ? "border-rose-400 ring-1 ring-rose-200" : ""}>
              <SelectValue placeholder="Select company…" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NO_PROJECT}>— Select company —</SelectItem>
              {companies.map((c) => (
                <SelectItem key={c.id} value={c.id} data-testid={`company-option-${c.code}`}>
                  {c.code} — {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <FieldError testId="company-error">{errors.company}</FieldError>
        </div>

        {/* Department — required */}
        <div className="space-y-2">
          <Label className="text-slate-700">Department<Required /></Label>
          <Select value={departmentId}
            onValueChange={(v) => { setDepartmentId(v); setTouched((t) => ({ ...t, department: true })); }}>
            <SelectTrigger data-testid="department-select"
              onBlur={() => setTouched((t) => ({ ...t, department: true }))}
              className={errors.department ? "border-rose-400 ring-1 ring-rose-200" : ""}>
              <SelectValue placeholder="Select department…" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NO_PROJECT}>— Select department —</SelectItem>
              {departments.map((p) => (
                <SelectItem key={p.id} value={p.id} data-testid={`department-option-${p.code}`}>
                  {p.code} — {p.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <FieldError testId="department-error">{errors.department}</FieldError>
        </div>

        {/* Comment — required */}
        <div className="space-y-2">
          <Label htmlFor="ex-comment" className="text-slate-700">Comment<Required /></Label>
          <Textarea id="ex-comment" value={comment}
            onChange={(e) => { setComment(e.target.value); setTouched((t) => ({ ...t, comment: true })); }}
            onBlur={() => setTouched((t) => ({ ...t, comment: true }))}
            placeholder="What was this for?"
            maxLength={1000} rows={3}
            className={errors.comment ? "border-rose-400 focus-visible:ring-rose-200" : ""}
            data-testid="expense-comment-input" />
          <FieldError testId="comment-error">{errors.comment}</FieldError>
        </div>

        {/* TMS — required (at least one across the three) OR override */}
        <div className="space-y-2" data-testid="tms-block">
          <label className={`mt-2 flex items-start gap-2 rounded-md border px-3 py-2 text-sm transition-colors ${
            tmsOverride
              ? "border-[#0F1E3D] border-l-4 bg-[#0F1E3D]/5 text-slate-800"
              : "border-slate-200 bg-white text-slate-700"
          }`}>
            <input type="checkbox" checked={tmsOverride}
              onChange={(e) => setTmsOverride(e.target.checked)}
              className="mt-0.5"
              data-testid="tms-override-toggle" />
            <span>
              <span className="font-medium">This expense doesn&apos;t need a truck, trailer, or driver</span>
              <span className="block text-xs text-slate-500">Tick to skip the TMS link requirement.</span>
            </span>
          </label>
          {!tmsOverride && (
            <>
              <div>
                <Label className="text-slate-700">Link to TMS<Required /></Label>
                <p className="mt-0.5 text-xs text-slate-500">Select at least one truck, trailer, or driver.</p>
              </div>
              <div className="space-y-2">
                {TMS_KINDS.map((kind) => {
                  const items = kind.key === "trucks" ? trucks : kind.key === "trailers" ? trailers : drivers;
                  return (
                    <TmsSection key={kind.key} kind={kind} items={items}
                      onAdd={(it) => addTo(kind.key, it)}
                      onRemove={(id) => removeFrom(kind.key, id)} />
                  );
                })}
              </div>
            </>
          )}
          {errors.tms && !tmsOverride && (
            <Alert variant="destructive" data-testid="tms-error">
              <AlertDescription>{errors.tms}</AlertDescription>
            </Alert>
          )}
        </div>

        {/* Charge type — visible only when override is off and equipment is linked.
            Rules (Phase 11):
              - No truck AND no trailer linked          → section hidden, charge_type stays internal
              - Truck OR trailer linked, no driver      → Internal / Charge to Lessee
              - Driver linked (± truck/trailer)         → Internal / Charge to Driver / Charge to Lessee */}
        {!tmsOverride && (trucks.length + trailers.length + drivers.length > 0) && (
          (() => {
            const hasEquipment = trucks.length + trailers.length > 0;
            const hasDriver = drivers.length > 0;
            const showLessee = hasEquipment;        // requires truck/trailer
            const showDriver = hasDriver;           // requires driver
            if (!showLessee && !showDriver) return null;
            // (Only-driver case: no equipment → lessee unavailable; only driver radio + internal.)

            const OPTIONS = [
              { key: "internal", label: "Internal cost", desc: "Company-paid expense (default)." },
              showDriver ? { key: "driver", label: "Charge to Driver", desc: "Deduct from the linked driver(s) via Alvys." } : null,
              showLessee ? { key: "lessee", label: "Charge to Lessee", desc: "Bill back to a lessee company (out-of-band invoicing)." } : null,
            ].filter(Boolean);

            return (
              <div className="space-y-2" data-testid="charge-type-block">
                <Label className="text-slate-700">Charge type</Label>
                <div role="radiogroup" className="grid gap-2 sm:grid-cols-2" data-testid="charge-type-options">
                  {OPTIONS.map((opt) => {
                    const checked = chargeType === opt.key;
                    return (
                      <label
                        key={opt.key}
                        data-testid={`charge-type-option-${opt.key}`}
                        className={`flex cursor-pointer items-start gap-2 rounded-md border px-3 py-2 text-sm transition-colors ${
                          checked
                            ? "border-[#0F1E3D] border-l-4 bg-[#0F1E3D]/5 text-slate-800"
                            : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                        }`}>
                        <input
                          type="radio"
                          name="charge-type"
                          value={opt.key}
                          checked={checked}
                          onChange={() => {
                            setAutoRevertNotice("");
                            setChargeType(opt.key);
                            if (opt.key !== "lessee") setLesseeName("");
                          }}
                          data-testid={`charge-type-radio-${opt.key}`}
                          className="mt-0.5"
                        />
                        <span>
                          <span className="font-medium">{opt.label}</span>
                          <span className="block text-xs text-slate-500">{opt.desc}</span>
                        </span>
                      </label>
                    );
                  })}
                </div>

                {chargeType === "lessee" && (
                  <div className="rounded-md border border-amber-200 bg-amber-50/40 p-3" data-testid="lessee-input-block">
                    <Label htmlFor="lessee-name-input" className="text-slate-700">Lessee name<Required /></Label>
                    <Input
                      id="lessee-name-input"
                      value={lesseeName}
                      onChange={(e) => setLesseeName(e.target.value.slice(0, 200))}
                      placeholder="e.g., ACME Logistics LLC"
                      maxLength={200}
                      className="mt-1.5"
                      data-testid="lessee-name-input"
                    />
                    {attemptedSubmit && chargeType === "lessee" && !lesseeName.trim() && (
                      <p className="mt-1 text-xs text-rose-600" data-testid="lessee-name-error">Lessee name is required.</p>
                    )}
                  </div>
                )}

                {autoRevertNotice && (
                  <p className="text-xs text-amber-700" data-testid="charge-type-auto-revert-notice">{autoRevertNotice}</p>
                )}
              </div>
            );
          })()
        )}

        {/* Receipt */}
        <div className="space-y-2">
          <Label className="text-slate-700">Receipt (optional)</Label>
          {receiptPreview ? (
            <div className="flex items-center gap-3 rounded-md border border-slate-200 bg-white p-3" data-testid="receipt-preview">
              <img src={receiptPreview} alt="Receipt" className="h-20 w-20 rounded object-cover" />
              <div className="flex-1">
                <p className="text-sm font-medium text-slate-700">Receipt attached</p>
                <Button type="button" variant="ghost" size="sm" onClick={removeReceipt} data-testid="receipt-remove-btn">
                  <X className="mr-1 h-4 w-4" /> Remove
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <Button type="button" variant="outline" onClick={onPickFile}
                disabled={uploadingReceipt} data-testid="receipt-upload-btn">
                <Camera className="mr-2 h-4 w-4" />
                {uploadingReceipt ? "Uploading…" : "Add receipt"}
              </Button>
              <span className="text-xs text-slate-500">JPG, PNG, WebP, HEIC, PDF · max 10MB image / 15MB PDF</span>
            </div>
          )}
          <input ref={fileRef} type="file"
            accept="image/jpeg,image/png,image/webp,image/heic,application/pdf,.pdf,.jpg,.jpeg,.png,.webp,.heic"
            capture="environment" className="hidden"
            onChange={onFileChange} data-testid="receipt-file-input" />
        </div>

        {formError && (
          <Alert variant="destructive" data-testid="expense-form-error">
            <AlertDescription>{formError}</AlertDescription>
          </Alert>
        )}

        {/* Sticky submit */}
        <div className="fixed inset-x-0 bottom-0 z-20 border-t border-slate-200 bg-white p-4 sm:static sm:border-0 sm:bg-transparent sm:p-0">
          <div className="mx-auto flex max-w-7xl items-center justify-end gap-2">
            <Button type="button" variant="ghost" onClick={() => navigate(-1)} disabled={submitting}>Cancel</Button>
            <Button type="submit" disabled={!submitEnabled}
              data-testid="expense-submit-btn"
              className="bg-[#0F1E3D] text-white hover:bg-[#0F1E3D]/90 disabled:cursor-not-allowed disabled:opacity-50">
              {submitting ? "Saving…" : isEdit ? "Save changes" : "Log expense"}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}
